/* eslint-disable react/require-default-props */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ReactElement } from 'react';

import axios from 'axios';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { IoCloseSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { ModalInfo, Button, Input, InputMask } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IProfessionalForm } from '~/models/User';
import { CollaboratorsService, ProfessionalService } from '~/services';
import { ICollaboratorEdit } from '~/services/Collaborators';
import { getCEP, removeMark } from '~/utils';

import * as S from './styles';

interface ICollaboratorModalProps {
  modalIsVisible: boolean;
  trainerId: number;
  userTrainerId: number;
  setModalIsVisible: Dispatch<SetStateAction<boolean>>;
  editForm: boolean;
}

export interface ICollaboratorChange {
  public_name: string;
  name: string;
  cpf_cnpj: string;
  professional_register: string;
  date_of_birth: string;
  email: string;
  phone: string;
  cep: string;
  address: string;
  number: string;
  complement?: string;
  city: string;
  UF: string;
  country: string;
  role: string;
}

function TrainerModal({
  editForm,
  modalIsVisible,
  trainerId,
  userTrainerId,
  setModalIsVisible,
}: ICollaboratorModalProps): ReactElement {
  const { setLoading } = useLoading();

  const [isRemovingCollaborator, setIsRemovingCollaborator] = useState(false);
  const [collaboratorRole, setCollaboratorRole] = useState<ICollaboratorEdit>();
  const [trainer, setTrainer] = useState<IProfessionalForm>({
    role: 'professional',
    name: '',
    public_name: '',
    cpf: '',
    cref: '',
    birth_date: '',
    email: '',
    cellphone: '',
    address_zip_code: '',
    address_country: 'Brasil',
    address_state: '',
    address_city: '',
    address_neighborhood: '',
    address_number: '',
    address_complement: '',
    address_street: '',
  });

  const [cep, setCep] = useState('');

  const handleRemoveCollaborator = useCallback(async () => {
    try {
      setLoading(true);

      setIsRemovingCollaborator(true);
      const response = await CollaboratorsService.removeCollaborator(
        userTrainerId,
      );

      setLoading(false);
      if (response) toast.success('Colaborador removido com sucesso!');
    } catch (err) {
      console.log(err);
      toast.error('Houve um erro ao remover o colaborador');
      setLoading(false);
    } finally {
      setIsRemovingCollaborator(false);
      setModalIsVisible(false);
      setLoading(false);
    }
  }, [userTrainerId]);

  const handleGetCollaborator = useCallback(async () => {
    if (trainerId === -1) return;
    try {
      setLoading(true);

      const { data } = await CollaboratorsService.getOnlyCollaborator(
        trainerId,
      );

      if (data) setCollaboratorRole(data);

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  }, [trainerId]);

  useEffect(() => {
    handleGetCollaborator();
  }, [trainerId, handleGetCollaborator]);

  const handleSubmit = useCallback(async () => {
    if (
      !trainer ||
      !trainer.name ||
      !trainer.public_name ||
      !trainer.cpf ||
      !trainer.birth_date ||
      !trainer.cref ||
      !trainer.email ||
      !trainer.cellphone ||
      !trainer.address_zip_code ||
      !trainer.address_street ||
      !trainer.address_number ||
      !trainer.address_neighborhood ||
      !trainer.address_city ||
      !trainer.address_state ||
      !trainer.address_country
    ) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (trainer.cpf.length !== 11 && trainer.cpf.length !== 14) {
      toast.error('CPF/CNPJ inválido');
      return;
    }

    if (trainer.cpf.length === 11 && !cpf.isValid(trainer.cpf)) {
      toast.error('CPF inválido');
      return;
    }

    if (trainer.cpf.length === 14 && !cnpj.isValid(trainer.cpf)) {
      toast.error('CNPJ inválido');
      return;
    }

    const birthDate = new Date(trainer.birth_date);
    birthDate.setDate(birthDate.getDate() + 1);

    const currentDate = new Date();

    if (birthDate > currentDate) {
      toast.error(
        'Data de nascimento inválida. Não pode ser hoje ou no futuro.',
      );
      return;
    }

    try {
      setLoading(true);

      if (editForm) {
        // const response = await ProfessionalService.updateCollaborator(
        //   trainerId,
        //   trainer,
        // );
        // if (response) toast.success('Colaborador editado com sucesso!');
        // setModalIsVisible(false);
      } else {
        const response = await ProfessionalService.createProfessional(trainer);
        if (response) toast.success('Treinador criado com sucesso!');
        setModalIsVisible(false);
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data.message);
      } else {
        toast.error('Ocorreu um erro');
      }
    } finally {
      setLoading(false);
    }
  }, [trainer, setLoading, editForm, setModalIsVisible]);

  // useEffect(() => {
  //   if (collaboratorRole && editForm) {
  //     setCollaborator({
  //       email: collaboratorRole.collaborator.user.email,
  //       modules: collaboratorRole.collaborator.modules,
  //       name: collaboratorRole.collaborator.user.name,
  //       observations: collaboratorRole.collaborator.observations,
  //       password: collaboratorRole.dec_password,
  //     });
  //   } else {
  //     setCollaborator({
  //       name: '',
  //       email: '',
  //       password: '',
  //       modules: [],
  //       observations: '',
  //     });
  //   }
  // }, [collaboratorRole, editForm]);
  useEffect(() => {
    const getAddress = async () => {
      if (trainer.address_zip_code.length !== 8) {
        return;
      }
      try {
        setLoading(true);
        const data = await getCEP(cep);
        if (data) {
          setTrainer((prevState) => ({
            ...prevState,
            address_city: data.address_city,
            address_state: data.address_state,
            address_street: data.address_street,
            address_complement: data.address_complement,
            address_neighborhood: data.address_neighborhood,
          }));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getAddress();
  }, [cep, setLoading, trainer.address_zip_code.length]);

  return (
    <ModalInfo
      modalVisible={modalIsVisible}
      setModalVisible={setModalIsVisible}
      containerStyle={{
        justifyContent: 'flex-start',
        padding: '3rem',
        alignItems: 'flex-start',
        maxWidth: '75rem',
      }}
    >
      <S.ConfirmDeleteContainer>
        <S.HeaderContainer>
          <S.ConfirmDeleteTitle>
            {editForm ? 'Treinador' : 'Cadastrar treinador'}
          </S.ConfirmDeleteTitle>
          <IoCloseSharp
            style={{ cursor: 'pointer' }}
            size={30}
            onClick={() => setModalIsVisible(!modalIsVisible)}
          />
        </S.HeaderContainer>
        <S.Divider />
        <S.FormWrapper
          onSubmit={() => {
            console.log('trainer');
          }}
        >
          <S.Body>
            <S.Side>
              <Input
                name="trainer_public_name"
                label="Nome público"
                placeholder="Nome público"
                value={trainer.public_name}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, public_name: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_name"
                label="Nome"
                placeholder="Nome"
                value={trainer.name}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, name: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <S.EditUserInputBlock>
                <p>CPF/CNPJ</p>
                <Input
                  name="trainer_cpf_cnpj"
                  value={trainer.cpf}
                  maxLength={14}
                  placeholder="00000000000"
                  onChange={({ target }) => {
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        cpf: removeMark(target.value),
                      };
                    });
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
            <S.Side>
              <Input
                name="trainer_professional_register"
                label="Registro profissional"
                value={trainer.cref}
                placeholder="00000000000"
                type="number"
                min={0}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return {
                      ...prevState,
                      cref: target.value,
                    };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_birth_of_date"
                // value={trainer.birth_date}
                label="Data de nascimento"
                placeholder="00/00/0000"
                type="date"
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, birth_date: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_email"
                label="Email"
                type="email"
                placeholder="E-mail"
                value={trainer.email}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, email: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <S.EditUserInputBlock>
                <p>Celular</p>
                <InputMask
                  name="trainer_phone"
                  value={trainer.cellphone}
                  placeholder="(00) 00000-0000"
                  onChange={({ target }) =>
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        cellphone: removeMark(target.value),
                      };
                    })
                  }
                  mask="(99) 99999-9999"
                />
              </S.EditUserInputBlock>
            </S.Side>
            <S.Side>
              <S.EditUserInputBlock>
                <p>CEP</p>
                <InputMask
                  name="trainer_cep"
                  value={trainer.address_zip_code}
                  placeholder="00000-000"
                  onChange={({ target }) => {
                    setCep(target.value);
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        address_zip_code: removeMark(target.value),
                      };
                    });
                  }}
                  mask="99999-999"
                />
              </S.EditUserInputBlock>
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_address"
                label="Endereço"
                placeholder="Endereço"
                value={trainer.address_street}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_street: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_number"
                label="Número"
                placeholder="Número"
                type="number"
                min={1}
                value={trainer.address_number}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_number: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_complement"
                label="Complemento (Opcional)"
                placeholder="Complemento"
                value={trainer.address_complement}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_complement: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_neighborhood"
                label="Bairro"
                placeholder="Bairro"
                value={trainer.address_neighborhood}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_neighborhood: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_city"
                label="Cidade"
                placeholder="Cidade"
                value={trainer.address_city}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_city: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_uf"
                label="Estado"
                placeholder="Estado"
                value={trainer.address_state}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_state: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_country"
                label="País"
                placeholder="País"
                value={trainer.address_country}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, address_country: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
        </S.FormWrapper>
        <S.ConfirmDeleteButtonGroup>
          {!editForm && (
            <Button
              size="small"
              textContent="Cancelar"
              onClick={() => setModalIsVisible(!modalIsVisible)}
              variant="red"
              extraStyles={{
                maxWidth: '200px',
                maxHeight: 35,
              }}
            />
          )}
          <Button
            size="small"
            textContent={editForm ? 'Salvar' : 'Cadastrar'}
            onClick={handleSubmit}
            variant="black"
            extraStyles={{
              maxWidth: '200px',
              maxHeight: 35,
              marginLeft: 17,
            }}
          />
        </S.ConfirmDeleteButtonGroup>
      </S.ConfirmDeleteContainer>
    </ModalInfo>
  );
}

export default TrainerModal;
